import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Typography, Button, Input, Textarea, Checkbox, Chip } from "@material-tailwind/react";
import ReactSelect from 'react-select';
import { FaMoneyBillWave, FaArrowRight, FaSpinner, FaHistory, FaSearch } from 'react-icons/fa';
import axios from 'axios';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import { formatMoney } from './formatMoney';

const OwnerPaymentAllocation = () => {
    const { token, user } = useAuth();
    const [owners, setOwners] = useState([]);
    const [cashbookAccounts, setCashbookAccounts] = useState([]);
    const [unallocatedTrans, setUnallocatedTrans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [selectedTrans, setSelectedTrans] = useState(new Set());
    const [message, setMessage] = useState({ type: '', text: '' });

    const [formData, setFormData] = useState({
        owner_code: '',
        payment_type: 'PAY', // 'PAY' (Out to Owner) or 'RIM' (In from Owner)
        date: new Date().toISOString().split('T')[0],
        amount: '',
        payment_method: 'MPESA',
        payment_reference: '',
        cashbook_account_id: '',
        notes: '',
        week_key: ''
    });

    const paymentMethods = [
        { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
        { value: 'CHEQUE', label: 'Cheque' },
        { value: 'MPESA', label: 'M-Pesa' },
        { value: 'CASH', label: 'Cash' }
    ];

    const fetchOwners = async () => {
        try {
            const res = await axios.get(`${rootUrl}/owners`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOwners(res.data || []);
        } catch (error) {
            console.error('Error fetching owners:', error);
        }
    };

    const fetchCashbookAccounts = async () => {
        try {
            const res = await axios.get(`${rootUrl}/api/cashbook/accounts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setCashbookAccounts(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching cashbook accounts:', error);
        }
    };

    const fetchUnallocated = useCallback(async () => {
        if (!formData.owner_code) return;
        setLoading(true);
        try {
            const res = await axios.get(`${rootUrl}/api/owners/${formData.owner_code}/unallocated-transactions?type=${formData.payment_type}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setUnallocatedTrans(res.data.data);
                setSelectedTrans(new Set());
            }
        } catch (error) {
            console.error('Error fetching unallocated transactions:', error);
        } finally {
            setLoading(false);
        }
    }, [formData.owner_code, formData.payment_type, token]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchOwners();
        fetchCashbookAccounts();
    }, [token]);

    useEffect(() => {
        if (formData.owner_code) {
            fetchUnallocated();
        } else {
            setUnallocatedTrans([]);
        }
    }, [formData.owner_code, formData.payment_type, fetchUnallocated]);

    const handleSelectTrans = (id) => {
        const newSelected = new Set(selectedTrans);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedTrans(newSelected);

        // Auto-calculate total amount based on selection
        const total = Array.from(newSelected).reduce((sum, transId) => {
            const trans = unallocatedTrans.find(t => t.id === transId);
            return sum + (parseFloat(trans?.balance_remaining) || 0);
        }, 0);
        setFormData(prev => ({ ...prev, amount: total.toFixed(2) }));
    };

    const handleSubmit = async () => {
        if (!formData.owner_code || !formData.amount || !formData.cashbook_account_id) {
            setMessage({ type: 'error', text: 'Please fill in all required fields' });
            return;
        }

        const selectedList = Array.from(selectedTrans).map(id => {
            const trans = unallocatedTrans.find(t => t.id === id);
            return {
                trans_id: id,
                amount: parseFloat(trans.balance_remaining)
            };
        });

        if (selectedList.length === 0) {
            setMessage({ type: 'error', text: 'Please select at least one transaction to allocate' });
            return;
        }

        setProcessing(true);
        setMessage({ type: '', text: '' });

        try {
            const payload = {
                ...formData,
                amount: parseFloat(formData.amount),
                allocations: selectedList,
                input_by: user?.username || 'system'
            };

            const res = await axios.post(`${rootUrl}/api/owners/process-allocation`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                setMessage({ type: 'success', text: 'Payment and allocation processed successfully!' });
                setFormData(prev => ({ ...prev, amount: '', notes: '', payment_reference: '' }));
                setSelectedTrans(new Set());
                fetchUnallocated();
            } else {
                setMessage({ type: 'error', text: res.data.message || 'Processing failed' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Server error' });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Owner Payment & Allocation</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Process payouts to owners or receive payments and link them to account entries
                    </p>
                </div>

                <div className="w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column: Form */}
                    <Card className="lg:col-span-1 bg-white shadow-xl rounded-2xl h-fit">
                        <CardBody className="p-6">
                            <Typography variant="h5" color="blue-gray" className="mb-6 flex items-center gap-2">
                                <FaMoneyBillWave className="text-[#FF5733]" />
                                Payment Details
                            </Typography>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Owner</label>
                                    <ReactSelect
                                        options={owners.map(o => ({ value: o.code, label: `${o.name} (${o.code})` }))}
                                        value={owners.find(o => o.code === formData.owner_code) ? { value: formData.owner_code, label: owners.find(o => o.code === formData.owner_code).name } : null}
                                        onChange={(opt) => setFormData({ ...formData, owner_code: opt?.value || '' })}
                                        placeholder="Select Owner..."
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Direction</label>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setFormData({ ...formData, payment_type: 'PAY' })}
                                            className={`flex-1 py-2 rounded-sm text-xs font-bold transition-all ${formData.payment_type === 'PAY' ? 'bg-[#FF5733] text-white' : 'bg-gray-100 text-gray-600'}`}
                                        >
                                            PAY OUT (To Owner)
                                        </button>
                                        <button
                                            onClick={() => setFormData({ ...formData, payment_type: 'RIM' })}
                                            className={`flex-1 py-2 rounded-sm text-xs font-bold transition-all ${formData.payment_type === 'RIM' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                                        >
                                            PAY IN (From Owner)
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Date</label>
                                    <Input
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="!border-t-blue-gray-200 focus:!border-[#FF5733]"
                                        labelProps={{ className: "hidden" }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Amount</label>
                                    <Input
                                        type="number"
                                        value={formData.amount}
                                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                        className="!border-t-blue-gray-200 focus:!border-[#FF5733] text-right font-bold"
                                        labelProps={{ className: "hidden" }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Payment Method</label>
                                    <ReactSelect
                                        options={paymentMethods}
                                        value={paymentMethods.find(m => m.value === formData.payment_method)}
                                        onChange={(opt) => setFormData({ ...formData, payment_method: opt?.value })}
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Cashbook Account</label>
                                    <ReactSelect
                                        options={cashbookAccounts.map(a => ({ value: a.id, label: `${a.account_name} (${a.account_type})` }))}
                                        value={cashbookAccounts.find(a => a.id === formData.cashbook_account_id) ? { value: formData.cashbook_account_id, label: cashbookAccounts.find(a => a.id === formData.cashbook_account_id).account_name } : null}
                                        onChange={(opt) => setFormData({ ...formData, cashbook_account_id: opt?.value })}
                                        classNamePrefix="react-select"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Reference / Notes</label>
                                    <Textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        className="!border-t-blue-gray-200 focus:!border-[#FF5733]"
                                        labelProps={{ className: "hidden" }}
                                    />
                                </div>

                                {message.text && (
                                    <div className={`p-3 rounded-lg text-sm font-semibold ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                        {message.text}
                                    </div>
                                )}

                                <Button
                                    fullWidth
                                    onClick={handleSubmit}
                                    disabled={processing || !formData.owner_code || !formData.amount}
                                    className="bg-[#FF5733] hover:bg-[#e64520] shadow-none flex items-center justify-center gap-2"
                                >
                                    {processing ? <FaSpinner className="animate-spin" /> : <FaArrowRight />}
                                    Process & Allocate
                                </Button>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Right Column: Allocation Table */}
                    <Card className="lg:col-span-2 bg-white shadow-xl rounded-2xl overflow-hidden">
                        <div className="bg-slate-50 p-6 border-b border-gray-100 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
                            <div>
                                <Typography variant="h5" color="blue-gray" className="flex items-center gap-2">
                                    <FaHistory className="text-[#FF5733]" />
                                    Unallocated Transactions
                                </Typography>
                                <Typography variant="small" className="text-gray-500 font-normal">
                                    Select existing entries to cover with this payment
                                </Typography>
                            </div>
                            <Chip
                                value={`${selectedTrans.size} Selected`}
                                className="rounded-full bg-blue-50 text-blue-700 self-start sm:self-auto"
                            />
                        </div>

                        <CardBody className="p-0">
                            {loading ? (
                                <div className="p-12 text-center text-gray-500 flex flex-col items-center gap-3">
                                    <FaSpinner className="animate-spin text-3xl text-[#FF5733]" />
                                    <span>Fetching transactions...</span>
                                </div>
                            ) : unallocatedTrans.length === 0 ? (
                                <div className="p-12 text-center text-gray-500 flex flex-col items-center gap-4">
                                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center">
                                        <FaSearch className="text-gray-300 text-2xl" />
                                    </div>
                                    <div>
                                        <Typography variant="h6" className="text-gray-400">No transactions found</Typography>
                                        <Typography variant="small">Select an owner to see their open account entries</Typography>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-3 px-4 py-4 md:hidden">
                                        {unallocatedTrans.map((t) => (
                                            <div
                                                key={t.id}
                                                onClick={() => handleSelectTrans(t.id)}
                                                className={`rounded-2xl border p-4 shadow-sm transition-colors ${selectedTrans.has(t.id) ? 'bg-[#fff8f5] border-[#FF5733]/30' : 'bg-white border-gray-200 hover:bg-slate-50'}`}
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <label className="flex items-start gap-3">
                                                        <Checkbox
                                                            checked={selectedTrans.has(t.id)}
                                                            color="orange"
                                                            ripple={false}
                                                            className="hover:before:opacity-0"
                                                            containerProps={{ className: "p-0" }}
                                                        />
                                                        <div>
                                                            <Typography className="text-xs text-gray-500">{new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
                                                            <Typography className="font-mono text-[10px] text-gray-400">#{t.id}</Typography>
                                                        </div>
                                                    </label>
                                                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-bold ${t.sign === '+' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                                                        {t.trans_type}
                                                    </span>
                                                </div>

                                                <div className="mt-3">
                                                    <Typography className="text-xs font-semibold text-gray-800">{t.description}</Typography>
                                                    {t.plate_no && <Typography className="mt-1 text-[10px] text-gray-500">{t.plate_no}</Typography>}
                                                </div>

                                                <div className="mt-3 rounded-xl bg-slate-50 p-3">
                                                    <div className="flex items-center justify-between gap-3">
                                                        <span className="text-xs text-gray-500">Balance</span>
                                                        <Typography className="text-sm font-bold text-gray-800">{formatMoney(t.balance_remaining)}</Typography>
                                                    </div>
                                                    {parseFloat(t.allocated_amount) > 0 && (
                                                        <div className="mt-1 flex items-center justify-between gap-3">
                                                            <span className="text-[10px] text-orange-600">Partial</span>
                                                            <Typography className="text-[10px] text-orange-600">{formatMoney(t.allocated_amount)}</Typography>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                <div className="hidden overflow-x-auto md:block">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-slate-50 border-b border-gray-200">
                                            <tr>
                                                <th className="p-4 w-10"></th>
                                                <th className="p-4 font-bold text-xs text-gray-600 uppercase">Date</th>
                                                <th className="p-4 font-bold text-xs text-gray-600 uppercase">Type</th>
                                                <th className="p-4 font-bold text-xs text-gray-600 uppercase">Description</th>
                                                <th className="p-4 font-bold text-xs text-gray-600 uppercase text-right">Balance</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {unallocatedTrans.map((t) => (
                                                <tr
                                                    key={t.id}
                                                    onClick={() => handleSelectTrans(t.id)}
                                                    className={`cursor-pointer transition-colors ${selectedTrans.has(t.id) ? 'bg-[#fff8f5]' : 'hover:bg-slate-50'}`}
                                                >
                                                    <td className="p-4">
                                                        <Checkbox
                                                            checked={selectedTrans.has(t.id)}
                                                            color="orange"
                                                            ripple={false}
                                                            className="hover:before:opacity-0"
                                                            containerProps={{ className: "p-0" }}
                                                        />
                                                    </td>
                                                    <td className="p-4">
                                                        <Typography className="text-[11px] text-gray-500">{new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography>
                                                        <Typography className="font-mono text-[10px] text-gray-400">#{t.id}</Typography>
                                                    </td>
                                                    <td className="p-4">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${t.sign === '+' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                                                            {t.trans_type}
                                                        </span>
                                                    </td>
                                                    <td className="p-4">
                                                        <Typography className="text-xs text-gray-700 font-medium truncate max-w-[200px]">{t.description}</Typography>
                                                        {t.plate_no && <Typography className="text-[10px] text-gray-500">{t.plate_no}</Typography>}
                                                    </td>
                                                    <td className="p-4 text-right">
                                                        <Typography className="text-xs font-bold text-gray-800">{formatMoney(t.balance_remaining)}</Typography>
                                                        {parseFloat(t.allocated_amount) > 0 && (
                                                            <Typography className="text-[10px] text-orange-600">Partial: {formatMoney(t.allocated_amount)}</Typography>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-[#fff8f5] font-bold">
                                            <tr>
                                                <td colSpan="4" className="p-4 text-right text-slate-700">Total Selected:</td>
                                                <td className="p-4 text-right text-[#FF5733] text-lg">
                                                    {formatMoney(formData.amount)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default OwnerPaymentAllocation;
